import { Fragment, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '../../services/api'
import { Badge, Button, Card, EmptyState, Select, Spinner, Table } from '../../components/ui'
import { formatDateShort, formatDateTime, APTITUDE_LABELS, ORDER_STATUS } from '../../utils/format'
import type { PortalOrder } from '../../types/api'

const APTITUDE_COLORS: Record<string, string> = {
  PENDING: 'bg-gray-100 text-gray-600',
  FIT: 'bg-emerald-100 text-emerald-800',
  FIT_WITH_RESTRICTIONS: 'bg-amber-100 text-amber-800',
  UNFIT: 'bg-red-100 text-red-700',
}

/**
 * Estado de las órdenes preocupacionales de los trabajadores de la empresa:
 * solo información administrativa (aptitud), nunca detalle clínico.
 */
export function PortalWorkersPage() {
  const [status, setStatus] = useState('')
  const [expanded, setExpanded] = useState<string | null>(null)

  const orders = useQuery({
    queryKey: ['portal-orders', status],
    queryFn: async () => (await api.get<PortalOrder[]>('/portal/orders', { params: status ? { status } : {} })).data,
  })

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-ink">Trabajadores</h1>
          <p className="text-sm text-gray-500">Estado y resultado administrativo de los exámenes preocupacionales.</p>
        </div>
        <div className="w-44">
          <Select value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="">Todos los estados</option>
            {Object.entries(ORDER_STATUS).map(([v, s]) => (
              <option key={v} value={v}>{s.label}</option>
            ))}
          </Select>
        </div>
      </div>

      <Card>
        {orders.isLoading ? (
          <Spinner />
        ) : (orders.data?.length ?? 0) === 0 ? (
          <EmptyState message="Sin órdenes para el filtro seleccionado." />
        ) : (
          <Table headers={['Trabajador', 'Batería', 'Cargo', 'Estado', 'Resultado', 'Certificado', '']}>
            {(orders.data ?? []).map((o) => (
              <Fragment key={o.id}>
                <tr>
                  <td className="px-2 py-2">
                    <span className="font-medium">{o.worker.name}</span>
                    <span className="block font-mono text-xs text-gray-400">{o.worker.rut}</span>
                  </td>
                  <td className="px-2 py-2 text-gray-600">{o.battery.name}</td>
                  <td className="px-2 py-2 text-gray-600">{o.jobTitle ?? '—'}</td>
                  <td className="px-2 py-2">
                    <Badge className={ORDER_STATUS[o.status].className}>{ORDER_STATUS[o.status].label}</Badge>
                  </td>
                  <td className="px-2 py-2">
                    <Badge className={APTITUDE_COLORS[o.result]}>{APTITUDE_LABELS[o.result]}</Badge>
                  </td>
                  <td className="px-2 py-2">
                    {o.certificateCode ? (
                      <Link
                        to={`/verificar?codigo=${o.certificateCode}`}
                        target="_blank"
                        className="font-mono text-xs font-medium text-brand-700 hover:underline"
                        title="Ver y descargar el certificado verificable"
                      >
                        {o.certificateCode} ↗
                      </Link>
                    ) : (
                      <span className="text-xs text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-2 py-2">
                    <Button variant="ghost" onClick={() => setExpanded(expanded === o.id ? null : o.id)}>
                      {expanded === o.id ? 'Cerrar' : 'Detalle'}
                    </Button>
                  </td>
                </tr>
                {expanded === o.id && (
                  <tr>
                    <td colSpan={7} className="bg-soft/50 px-4 py-3">
                      <div className="grid gap-3 text-sm sm:grid-cols-2">
                        <div>
                          <p className="text-xs font-semibold uppercase text-gray-500">Citas de la orden</p>
                          {o.appointments.length === 0 ? (
                            <p className="text-gray-500">Aún sin citas agendadas.</p>
                          ) : (
                            <ul className="mt-1 space-y-1">
                              {o.appointments.map((a) => (
                                <li key={a.id} className="text-gray-700">
                                  {formatDateTime(a.startsAt)} · {a.service.name}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold uppercase text-gray-500">Resumen</p>
                          <p className="text-gray-700">Orden creada el {formatDateShort(o.createdAt)}</p>
                          {o.completedAt && <p className="text-gray-700">Completada el {formatDateShort(o.completedAt)}</p>}
                          {o.resultNotes && (
                            <p className="text-gray-700">
                              <span className="font-medium">Observaciones:</span> {o.resultNotes}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </Table>
        )}
      </Card>

      <p className="text-xs text-gray-400">
        Los certificados se validan públicamente con su código en la página de{' '}
        <Link to="/verificar" target="_blank" className="text-brand-700 hover:underline">verificación de certificados</Link>; desde ahí puedes imprimirlos o guardarlos en PDF.
      </p>
    </div>
  )
}
